import { useState, useEffect, useCallback } from "react";
import { notificationAPI } from "@/lib/api";

/**
 * Custom hook for managing notifications state and WebSocket
 */
export function useNotifications(user) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationAPI.getAll(),
        notificationAPI.getUnreadCount(),
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch notifications:', error.message);
      }
    }
  }, [user]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!user) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws/${user.user_id}`;
    
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log("WebSocket connected for notifications");
      }
      // Send ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (websocket.readyState === WebSocket.OPEN) {
          websocket.send("ping");
        }
      }, 30000);
      
      websocket.pingInterval = pingInterval;
    };
    
    websocket.onmessage = (event) => {
      if (event.data === "pong") return;
      
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === "notification") {
          setNotifications((prev) => [data.notification, ...prev]);
          setUnreadCount((prev) => prev + 1);
          
          // Show browser notification if permitted
          if (Notification.permission === "granted") {
            new Notification(data.notification.title, {
              body: data.notification.message,
              icon: "/logo.svg",
            });
          }
        } else if (data.type === "new_delivery") {
          // For partners: new delivery available
          setNotifications((prev) => [{
            id: `temp_${Date.now()}`,
            type: "new_delivery",
            title: "New Delivery Available",
            message: `${data.delivery.pickup_address} → ${data.delivery.drop_address}`,
            data: data.delivery,
            read: false,
            created_at: new Date().toISOString(),
          }, ...prev]);
          setUnreadCount((prev) => prev + 1);
        } else if (data.type === "tracking_update") {
          // For users: delivery status update
          setNotifications((prev) => [{
            id: `temp_${Date.now()}`,
            type: "delivery_update",
            title: `Delivery ${data.status.replace("_", " ").toUpperCase()}`,
            message: data.tracking_update?.message || `Status updated to ${data.status}`,
            data: { delivery_id: data.delivery_id },
            read: false,
            created_at: new Date().toISOString(),
          }, ...prev]);
          setUnreadCount((prev) => prev + 1);
        }
      } catch (parseError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('WebSocket message parse error:', parseError.message);
        }
      }
    };
    
    websocket.onclose = () => {
      if (websocket.pingInterval) {
        clearInterval(websocket.pingInterval);
      }
    };
    
    return () => {
      if (websocket.pingInterval) {
        clearInterval(websocket.pingInterval);
      }
      websocket.close();
    };
  }, [user]);

  // Request notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark single notification as read
  const markRead = useCallback(async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to mark notification as read:', error.message);
      }
    }
  }, []);

  // Mark all notifications as read
  const markAllRead = useCallback(async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to mark all notifications as read:', error.message);
      }
    }
  }, []);

  return {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
  };
}
